import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  DocumentCheckIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { HseGuideButton, HseModuleGuide } from "../../../../components/dashboard/hse/HseModuleGuide";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { useUserHasAccess } from "../../../../routes/roleOnly";
import { fetchEvaluations } from "../../../../api/hse/evaluations";
import { EvaluationLabelInfo } from "../../../../components/dashboard/hse/evaluation/EvaluationLabelInfo";
import {
  EVALUATION_TYPES,
  evaluationPath,
  formatDisplayDate,
  statusTone,
} from "../../../../components/dashboard/hse/evaluation/evaluationUtils";

export const HseKsEvaluationOverview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.session.currentUser);
  const isAdmin = currentUser?.roles?.some((role) => role.name === "admin");
  const canViewEvaluation = useUserHasAccess({ role: "hse_ks_evaluation", action: "read" });
  const canViewAll = isAdmin || canViewEvaluation;
  const [recent, setRecent] = useState([]);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    if (!canViewAll) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [reviews, audits] = await Promise.allSettled([
          fetchEvaluations({ kind: EVALUATION_TYPES.MANAGEMENT_REVIEW, per_page: 5 }),
          fetchEvaluations({ kind: EVALUATION_TYPES.INTERNAL_AUDIT, per_page: 5 }),
        ]);
        if (cancelled) return;
        const items = [
          ...(reviews.status === "fulfilled" ? reviews.value.data : []),
          ...(audits.status === "fulfilled" ? audits.value.data : []),
        ]
          .sort((a, b) => String(b.updated_at || b.held_on || "").localeCompare(String(a.updated_at || a.held_on || "")))
          .slice(0, 8);
        setRecent(items);
      } catch (_err) {
        setRecent([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canViewAll]);

  const cards = [
    {
      key: "kpi",
      title: t("hse.evaluation.nav.kpi"),
      hint: t("hse.evaluation.nav.kpi_hint"),
      path: "/dashboard/hse-ks/evaluation/kpi",
      icon: ChartBarIcon,
      color: "text-blue-600",
    },
    {
      key: "internal_audit",
      title: t("hse.evaluation.types.internal_audit"),
      hint: t("hse.evaluation.nav.audit_hint"),
      path: "/dashboard/hse-ks/evaluation/audits",
      icon: ClipboardDocumentCheckIcon,
      color: "text-emerald-600",
      infoKey: "internal_audit",
    },
    {
      key: "management_review",
      title: t("hse.evaluation.types.management_review"),
      hint: t("hse.evaluation.nav.review_hint"),
      path: "/dashboard/hse-ks/evaluation/management-review",
      icon: UserGroupIcon,
      color: "text-violet-600",
      infoKey: "management_review",
    },
    {
      key: "feedback",
      title: t("hse.evaluation.nav.feedback"),
      hint: t("hse.evaluation.nav.feedback_hint"),
      path: "/dashboard/hse-ks/evaluation/customer-feedback",
      icon: DocumentCheckIcon,
      color: "text-orange-600",
    },
  ];

  if (!canViewAll) {
    return (
      <HsePageChrome title={t("hse.evaluation.title")} subtitle={t("hse.evaluation.restricted")}>
        <div className="mx-auto mt-8 max-w-3xl px-4 sm:px-6">
          <div className="rounded-[18px] border border-slate-200 bg-white p-6 text-sm text-slate-600">
            {t("hse.evaluation.restricted_body")}
          </div>
        </div>
      </HsePageChrome>
    );
  }

  return (
    <HsePageChrome title={t("hse.evaluation.title")} subtitle={t("hse.evaluation.subtitle")}>
      <div className="mx-auto mt-8 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-4 flex justify-end">
          <HseGuideButton onClick={() => setGuideOpen(true)} />
        </div>
        <HseModuleGuide topic="evaluation" open={guideOpen} onClose={() => setGuideOpen(false)} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.key}
                role="link"
                tabIndex={0}
                onClick={() => navigate(card.path)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    navigate(card.path);
                  }
                }}
                className="cursor-pointer rounded-[18px] border border-slate-200 bg-white p-5 text-left shadow-[0_1px_2px_rgba(0,0,0,.04)] transition hover:shadow-md"
              >
                <Icon className={`mb-3 h-8 w-8 ${card.color}`} />
                <h2 className="text-base font-extrabold text-slate-900">
                  {card.infoKey ? (
                    <EvaluationLabelInfo label={card.title} infoKey={card.infoKey} />
                  ) : (
                    card.title
                  )}
                </h2>
                <p className="mt-1 text-sm text-slate-500">{card.hint}</p>
              </div>
            );
          })}
        </div>

        <section className="mt-8 overflow-hidden rounded-[18px] border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <h3 className="text-sm font-extrabold text-slate-900">{t("hse.evaluation.recent")}</h3>
          </div>
          {recent.length === 0 ? (
            <div className="px-5 py-10">
              <ResourceEmpty message={t("hse.evaluation.list.empty")} />
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {recent.map((item) => (
                <li key={`${item.kind || item.evaluation_type}-${item.id}`}>
                  <Link
                    to={evaluationPath(item)}
                    className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 hover:bg-slate-50"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-slate-900">{item.title}</p>
                      <p className="text-xs text-slate-500">
                        {t(`hse.evaluation.types.${item.kind || item.evaluation_type}`)}
                        {formatDisplayDate(item.held_on || item.scheduled_on)
                          ? ` · ${formatDisplayDate(item.held_on || item.scheduled_on)}`
                          : ""}
                      </p>
                    </div>
                    <span
                      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${statusTone(
                        item.status
                      )}`}
                    >
                      {t(`hse.evaluation.statuses.${item.status}`)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </HsePageChrome>
  );
};
